import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { getReportingTimeZone } from '@/lib/report-timezone';

export interface SupernovaListParams {
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  // JSON-encoded Record<string, string> of per-column filter values, keyed
  // by the same field names used for sortBy.
  filters?: string;
}

export interface SupernovaPagination {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

// Matched POS to Funding / Amount Mismatch — a POS sale + its card/EBT
// payment, joined to the processor's own funding-file row for that
// authorization. Both amounts are exposed so a mismatch is visibly
// explained (POS charged paymentAmount, funding shows fundingAmount).
export interface SupernovaMatchedRow {
  transactionId: string;
  dailyOrderNumber: string | null;
  createdAt: string;
  status: string;
  itemCount: number;
  orderTotal: string;
  authCode: string | null;
  cardLastFour: string | null;
  paymentAmount: string;
  fundingAmount: string;
  fundingDate: string;
  fundingTransactionNumber: string;
  fundingStatus: string | null;
}

// Unmatched in POS — a real POS sale + card/EBT payment with no counterpart
// anywhere in the funding file.
export interface SupernovaUnmatchedSourceRow {
  transactionId: string;
  dailyOrderNumber: string | null;
  createdAt: string;
  status: string;
  itemCount: number;
  orderTotal: string;
  authCode: string | null;
  cardLastFour: string | null;
  paymentAmount: string;
}

// Unmatched Backend - Funding — a funding-file row the processor settled
// that no POS sale accounts for.
export interface SupernovaUnmatchedFundingRow {
  id: string;
  transactionNumber: string;
  fundingDate: string;
  method: string | null;
  name: string | null;
  brand: string | null;
  lastFour: string;
  amount: string;
  authCode: string;
  status: string | null;
  orderId: string | null;
}

export interface SupernovaOrderRow {
  id: string;
  dailyOrderNumber: string | null;
  status: string;
  subtotal: string;
  tax: string;
  discount: string;
  total: string;
  locationId: string | null;
  createdAt: string;
}

export interface SupernovaDuplicatePaymentRow {
  paymentId: string;
  transactionId: string;
  dailyOrderNumber: string | null;
  authCode: string | null;
  cardLastFour: string | null;
  amount: string;
  method: string;
  createdAt: string;
}

export interface SupernovaItemRow {
  id: string;
  transactionId: string;
  dailyOrderNumber: string | null;
  productId: string;
  qty: string;
  unitPrice: string;
  lineTotal: string;
  tax: string;
  discount: string;
  createdAt: string;
}

// Funded Transactions — a raw row from the processor's own funding file
// (cc_funding_target_rows), not a pos.payments row.
export interface SupernovaFundedRow {
  id: string;
  transactionNumber: string;
  fundingDate: string;
  method: string | null;
  name: string | null;
  brand: string | null;
  lastFour: string;
  amount: string;
  authCode: string;
  status: string | null;
  orderId: string | null;
}

export interface SupernovaSummary {
  matched: { count: number; total: string };
  unmatchedSource: { count: number; total: string };
  unmatchedFunding: { count: number; total: string };
  amountMismatch: { count: number; total: string };
  duplicates: { count: number; total: string };
}

interface SupernovaListResponse<T> {
  data: T[];
  pagination: SupernovaPagination;
  range: Record<string, unknown>;
}

function useSupernovaReport<T>(key: string, path: string, params: SupernovaListParams, enabled = true) {
  // Attach the reporting timezone so the server buckets dateFrom/dateTo by the
  // business-local day (Fremont = Pacific), not UTC — otherwise picking 08/11
  // pulls in 08/10 evening. Keyed so a zone change refetches.
  const timeZone = getReportingTimeZone();
  return useQuery<SupernovaListResponse<T>>({
    queryKey: ['supernova-recon', key, params, timeZone],
    queryFn: () => api.get(path, { ...params, timeZone } as Record<string, string | number | undefined>),
    enabled,
  });
}

export const useSupernovaMatched = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaMatchedRow>('matched', '/supernova-recon/report/matched', params, enabled);

export const useSupernovaUnmatchedSource = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaUnmatchedSourceRow>(
    'unmatched-source',
    '/supernova-recon/report/unmatched-source',
    params,
    enabled,
  );

export const useSupernovaUnmatchedFunding = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaUnmatchedFundingRow>(
    'unmatched-funding',
    '/supernova-recon/report/unmatched-funding',
    params,
    enabled,
  );

export const useSupernovaAmountMismatch = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaMatchedRow>(
    'amount-mismatch',
    '/supernova-recon/report/amount-mismatch',
    params,
    enabled,
  );

export const useSupernovaDuplicateTransactions = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaDuplicatePaymentRow>(
    'duplicate-transactions',
    '/supernova-recon/report/duplicate-transactions',
    params,
    enabled,
  );

export const useSupernovaOrders = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaOrderRow>('orders', '/supernova-recon/report/orders', params, enabled);

export const useSupernovaExcluded = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaOrderRow>('excluded', '/supernova-recon/report/excluded', params, enabled);

export const useSupernovaVoided = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaOrderRow>('voided', '/supernova-recon/report/voided', params, enabled);

export const useSupernovaVoidedRecaptured = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaOrderRow>(
    'voided-recaptured',
    '/supernova-recon/report/voided-recaptured',
    params,
    enabled,
  );

export const useSupernovaItems = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaItemRow>('items', '/supernova-recon/report/items', params, enabled);

export const useSupernovaPayments = (params: SupernovaListParams, enabled = true) =>
  useSupernovaReport<SupernovaFundedRow>('payments', '/supernova-recon/report/payments', params, enabled);

export function useSupernovaSummary(params: Omit<SupernovaListParams, 'page' | 'perPage'>) {
  const timeZone = getReportingTimeZone();
  return useQuery<{ data: SupernovaSummary }>({
    queryKey: ['supernova-recon', 'summary', params, timeZone],
    queryFn: () =>
      api.get('/supernova-recon/report/summary', { ...params, timeZone } as Record<string, string | undefined>),
  });
}
